import React, { useState, useEffect, useRef, useCallback, useContext, createContext, useMemo } from 'react';
import askConfirm from './Confirm';
import { createPortal } from 'react-dom';
import api, { apiOrigin, getStaffToken } from './api';

// Showing somebody how to do a task.
//
// Explaining a task takes ten minutes, and it takes ten minutes again for the next person, and
// again next year when nobody can remember why the third step exists. Recording it once takes the
// same ten minutes and is on the task itself — this month's, next month's, and every client's copy
// of the same task — for whoever picks it up.
//
// The recording is the screen: what you clicked, in what order, with you saying why. That is what
// a written step misses.

const fmt = (s) => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  const m = Math.floor(n / 60);
  return `${m}:${String(n % 60).padStart(2, '0')}`;
};
const mb = (b) => `${((Number(b) || 0) / 1048576).toFixed(1)} MB`;
const when = (t) => {
  if (!t) return '';
  const d = new Date(String(t).replace(' ', 'T') + (String(t).endsWith('Z') ? '' : 'Z'));
  return isNaN(d) ? String(t).slice(0, 10) : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

// A video element cannot send an Authorization header, so the same login token goes in the query.
const videoSrc = (id) => `${apiOrigin}/api/howtos/${id}/video?t=${encodeURIComponent(getStaffToken() || '')}`;

// A recording made in the browser has no duration written in it.
//
// MediaRecorder writes the file as it goes and never goes back to fill in the header, so the player
// is handed something of unknown length: the clock reads 0:00, the scrubber is dead, and a how-to
// nobody can skip through is one nobody watches to find the one step they needed.
//
// The cure is the old one — ask to seek past the end, which makes the browser read to the end and
// work out the real length, then put it back to the start. Done once, on the way in.
function teachItsLength(el) {
  if (!el || el.dataset.lengthKnown) return;
  if (el.duration !== Infinity && el.duration > 0) return;
  el.dataset.lengthKnown = '1';
  const settle = () => {
    el.removeEventListener('timeupdate', settle);
    el.currentTime = 0;
  };
  el.addEventListener('timeupdate', settle);
  try { el.currentTime = 1e7; } catch { /* it will just have no scrubber */ }
}

// What a player says when it cannot play: nothing. This turns that into words.
const WHY = {
  1: 'Loading it was stopped.',
  2: 'The connection dropped while it was loading.',
  3: 'The recording is damaged and can’t be played.',
  4: 'It couldn’t be loaded — you may have been signed out, or it is no longer on the server.',
};

// Nothing about a screen recording works without these two, and they are missing in more browsers
// than you would think — every browser on iOS, for one.
const canRecordScreen = () =>
  typeof navigator !== 'undefined' && navigator.mediaDevices &&
  typeof navigator.mediaDevices.getDisplayMedia === 'function' && !!window.MediaRecorder;

// Twenty minutes. Long enough for anything worth recording, and short enough that a recording left
// running by accident is not a hundred megabytes of somebody's afternoon.
const MAX_SECS = 20 * 60;

function pickMime() {
  // VP9 first: screen recordings are mostly still text, which is exactly what it is good at — the
  // same five minutes is roughly half the size of VP8 and the words stay readable.
  const wanted = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  for (const t of wanted) {
    if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

// ── The bar that stays out of the way ─────────────────────────
//
// While it is recording, this is the entire interface: how long, how big, pause, stop. Dragged
// wherever it is least in shot, and remembered there — the corner that is clear on one screen is
// the corner covering the thing you meant to show on another.
const BAR_POS_KEY = 'hub.recordBar';
const readPos = () => {
  try { const p = JSON.parse(localStorage.getItem(BAR_POS_KEY) || 'null'); return (p && typeof p.x === 'number') ? p : null; }
  catch { return null; }
};

function RecordingBar({ phase, secs, bytes, onPause, onResume, onStop, subject }) {
  const [pos, setPos] = useState(() => readPos());
  const dragRef = useRef(null);

  // Dragged by the bar itself, so there is no handle to find. Kept on screen when the window is
  // resized smaller — a bar dragged off the edge is a recording that cannot be stopped.
  const clamp = useCallback((p) => ({
    x: Math.min(Math.max(8, p.x), Math.max(8, window.innerWidth - 300)),
    y: Math.min(Math.max(8, p.y), Math.max(8, window.innerHeight - 60)),
  }), []);

  useEffect(() => {
    const onResize = () => setPos(p => (p ? clamp(p) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  const onPointerDown = (e) => {
    // Not from the buttons — dragging off a Stop button is how a recording gets lost.
    if (e.target.closest('button')) return;
    const box = e.currentTarget.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - box.left, dy: e.clientY - box.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const next = clamp({ x: e.clientX - dragRef.current.dx, y: e.clientY - dragRef.current.dy });
    setPos(next);
  };
  const onPointerUp = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { if (pos) localStorage.setItem(BAR_POS_KEY, JSON.stringify(pos)); } catch { /* it will sit in the corner */ }
  };

  const style = pos
    ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
    : undefined;   // the corner it starts in, until it is moved

  return createPortal(
    <div className={`rec-bar ${phase === 'paused' ? 'paused' : ''}`} style={style}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
      title={`Recording “${subject.name}” — drag me out of the way`}>
      <span className={`rec-dot ${phase === 'paused' ? 'paused' : ''}`} />
      <span className="rec-clock">{fmt(secs)}</span>
      <span className="rec-size">{phase === 'paused' ? 'Paused' : bytes > 0 ? mb(bytes) : 'Recording'}</span>
      {phase === 'paused'
        ? <button className="rec-btn" onClick={onResume} title="Carry on">▶</button>
        : <button className="rec-btn" onClick={onPause} title="Pause">❚❚</button>}
      <button className="rec-btn rec-stop" onClick={onStop} title="Stop and look at it">■ Stop</button>
    </div>,
    document.body
  );
}

// ── The recorder ──────────────────────────────────────────────
function RecorderModal({ subject, repeats, onClose, onSaved }) {
  const [phase, setPhase] = useState('idle');       // idle | recording | paused | ready | saving
  const [secs, setSecs] = useState(0);
  const [bytes, setBytes] = useState(0);
  const [clip, setClip] = useState(null);           // { blob, url }
  const [title, setTitle] = useState(`How to: ${subject.name}`);
  // Only a task has other copies of itself. A recording about a client is about the client.
  const onTask = subject.kind === 'task';
  const [everyCopy, setEvery] = useState(onTask && !!repeats);
  const [withMic, setMic] = useState(true);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState(0);
  const [store, setStore] = useState(null);

  const mrRef = useRef(null);
  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const chunksRef = useRef([]);
  // The clock is worked out from timestamps rather than counted in ticks. The whole point of this
  // screen is that you go and use another window while it records, and a browser slows a background
  // tab's timers to about one a minute — counted ticks would report a five-minute recording as
  // five seconds long.
  const startedRef = useRef(0);
  const pausedAtRef = useRef(0);
  const pausedMsRef = useRef(0);
  const secsRef = useRef(0);
  const bytesRef = useRef(0);

  useEffect(() => {
    let alive = true;
    api.get('/howtos/status').then(r => { if (alive) setStore(r.data); }).catch(() => { /* it will still record */ });
    return () => { alive = false; };
  }, []);

  const elapsed = useCallback(() => {
    if (!startedRef.current) return 0;
    const paused = pausedMsRef.current + (pausedAtRef.current ? Date.now() - pausedAtRef.current : 0);
    return Math.round((Date.now() - startedRef.current - paused) / 1000);
  }, []);

  // One clock, running the whole time the modal is open. It only reads timestamps, so being
  // throttled in a background tab costs nothing but a slower-looking counter.
  useEffect(() => {
    if (phase !== 'recording' && phase !== 'paused') return undefined;
    const id = setInterval(() => { secsRef.current = elapsed(); setSecs(secsRef.current); }, 500);
    return () => clearInterval(id);
  }, [phase, elapsed]);

  const teardown = useCallback(() => {
    try { (streamRef.current ? streamRef.current.getTracks() : []).forEach(t => t.stop()); } catch { /* already gone */ }
    try { if (ctxRef.current) ctxRef.current.close(); } catch { /* already closed */ }
    streamRef.current = null; ctxRef.current = null;
  }, []);

  const finish = useCallback(() => {
    const mr = mrRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();   // the blob is put together in onstop
    else teardown();
  }, [teardown]);

  const start = async () => {
    setErr(''); setNote('');
    let display;
    try {
      // 12 frames a second. A person clicking through Xero is not motion, and the difference
      // between this and 30 is a file three times the size that reads exactly the same.
      display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 12, max: 15 } },
        audio: true,
      });
    } catch (e) {
      // Cancelling the picker is not an error, it is a change of mind.
      if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) return;
      setErr('This browser wouldn’t share the screen. Chrome or Edge on a computer can — a phone or tablet can’t.');
      return;
    }

    let mic = null;
    if (withMic) {
      try { mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }); }
      catch { setNote('The microphone was blocked, so this is the screen without narration. Allow the mic for this site if you want to talk over it.'); }
    }

    const tracks = [...display.getVideoTracks()];
    const audios = [...display.getAudioTracks(), ...(mic ? mic.getAudioTracks() : [])];
    if (audios.length > 1) {
      // Two sources — what the computer is playing and what you are saying — have to become one
      // track, or the recording keeps only the first.
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const dest = ctx.createMediaStreamDestination();
      audios.forEach(t => { try { ctx.createMediaStreamSource(new MediaStream([t])).connect(dest); } catch { /* a track that won't mix */ } });
      ctxRef.current = ctx;
      tracks.push(dest.stream.getAudioTracks()[0]);
    } else if (audios.length === 1) tracks.push(audios[0]);

    const stream = new MediaStream(tracks);
    streamRef.current = new MediaStream([...tracks, ...(mic ? mic.getAudioTracks() : []), ...display.getTracks()]);

    const mime = pickMime();
    const opts = { videoBitsPerSecond: 1500000, audioBitsPerSecond: 64000 };
    if (mime) opts.mimeType = mime;
    let mr;
    try { mr = new MediaRecorder(stream, opts); }
    catch { mr = new MediaRecorder(stream); }       // an older browser that will not be told
    chunksRef.current = [];
    bytesRef.current = 0;
    mr.ondataavailable = (e) => {
      if (!e.data || !e.data.size) return;
      chunksRef.current.push(e.data);
      // What it weighs so far. A recording that grows past what the server will accept has to be
      // stopped while it is still worth something — finding out at the end means finding out with
      // twenty minutes in your hand and nowhere to put it.
      bytesRef.current += e.data.size;
      setBytes(bytesRef.current);
    };
    mr.onstop = () => {
      teardown();
      const blob = new Blob(chunksRef.current, { type: (mr.mimeType || 'video/webm').split(';')[0] });
      setClip({ blob, url: URL.createObjectURL(blob) });
      setSecs(secsRef.current || elapsed());
      setPhase('ready');
    };
    // Chrome's own "Stop sharing" bar is where most people will stop, not the button on this
    // screen. It has to end the recording, or the file keeps growing with a frozen picture.
    display.getVideoTracks().forEach(t => { t.onended = () => { if (mrRef.current && mrRef.current.state !== 'inactive') mrRef.current.stop(); }; });

    // A chunk a second, so a recording that is interrupted still has everything up to the moment.
    mr.start(1000);
    mrRef.current = mr;
    startedRef.current = Date.now();
    pausedAtRef.current = 0; pausedMsRef.current = 0; secsRef.current = 0;
    setSecs(0); setPhase('recording');
  };

  // Long enough is long enough, and big enough is refused. Either way it is stopped rather than
  // truncated, so what has been recorded is still there to save.
  const capBytes = (store && store.max_mb ? store.max_mb : 150) * 1048576;
  useEffect(() => {
    if (phase !== 'recording') return;
    if (secs >= MAX_SECS) {
      finish();
      setNote('Stopped at twenty minutes. Save this part and record the rest as a second one — nobody watches a longer one anyway.');
    } else if (bytes >= capBytes * 0.98) {
      finish();
      setNote(`Stopped at ${store && store.max_mb ? store.max_mb : 150} MB, which is as large as a recording can be saved. Save this part and record the rest as a second one.`);
    }
  }, [secs, bytes, phase, finish, capBytes, store]);

  const pause = () => {
    const mr = mrRef.current;
    if (!mr || mr.state !== 'recording') return;
    mr.pause(); pausedAtRef.current = Date.now(); setPhase('paused');
  };
  const resume = () => {
    const mr = mrRef.current;
    if (!mr || mr.state !== 'paused') return;
    mr.resume();
    pausedMsRef.current += Date.now() - pausedAtRef.current;
    pausedAtRef.current = 0;
    setPhase('recording');
  };

  const redo = () => {
    if (clip) URL.revokeObjectURL(clip.url);
    setClip(null); setSecs(0); setProgress(0); setPhase('idle');
  };

  const download = () => {
    if (!clip) return;
    const a = document.createElement('a');
    a.href = clip.url;
    a.download = `${title.replace(/[^\w\s.-]/g, '').trim() || 'how-to'}.webm`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const save = async () => {
    if (!clip) return;
    setPhase('saving'); setErr(''); setProgress(0);
    try {
      const r = await api.post(subject.url, clip.blob, {
        headers: {
          'Content-Type': clip.blob.type || 'video/webm',
          'X-Howto-Title': encodeURIComponent(title.trim() || `How to: ${subject.name}`),
          'X-Howto-Type': clip.blob.type || 'video/webm',
          'X-Howto-Seconds': String(secs || 0),
          'X-Howto-Scope': everyCopy ? 'series' : 'task',
        },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      if (r.data && r.data.no_template) {
        window.alert('Saved. This workflow didn’t come from a template, so the recording stays with this task and the copies of it that follow — there are no other clients’ copies to put it on.');
      }
      if (clip) URL.revokeObjectURL(clip.url);
      onSaved(r.data);
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || 'That recording couldn’t be saved. It is still here — download it before closing this.');
      setPhase('ready');
    }
  };

  const close = async () => {
    if (phase === 'recording' || phase === 'paused') {
      if (!await askConfirm({ body: 'Stop recording and throw it away?', label: 'Stop and discard' })) return;
      finish();
    }
    if (phase === 'ready' && clip) {
      if (!await askConfirm({ body: 'Close without saving this recording? It will be lost.', label: 'Close and lose it' })) return;
    }
    teardown();
    if (clip) URL.revokeObjectURL(clip.url);
    onClose();
  };

  useEffect(() => () => { teardown(); }, [teardown]);

  const live = phase === 'recording' || phase === 'paused';

  // Recording is not a dialog.
  //
  // A modal is right for choosing what to record and for naming it afterwards; while it is actually
  // recording it is the one thing that must not be in the way, because the whole point is to be
  // seen doing the task. So the moment it starts, the box and its backdrop go, and what is left is
  // a small bar you can drag out of shot. Everything underneath stays live and clickable.
  if (live) return <RecordingBar phase={phase} secs={secs} bytes={bytes} onPause={pause} onResume={resume} onStop={finish} subject={subject} />;

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal howto-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">🎬 Record how this is done</div>
        <div className="howto-modal-task">{subject.name}</div>

        {!canRecordScreen() ? (
          <div className="howto-err">
            This browser can’t record the screen. Chrome or Edge on a computer can — recording isn’t
            possible on a phone or tablet.
          </div>
        ) : (
          <>
            {phase === 'idle' && (
              <>
                <p className="howto-help">
                  You’ll be asked which screen, window or tab to record. Do the task as you normally
                  would and talk through it — whoever picks this up next will watch it here, on the task.
                </p>
                <label className="howto-check">
                  <input type="checkbox" checked={withMic} onChange={e => setMic(e.target.checked)} />
                  <span>Record my voice as well</span>
                </label>
                <p className="howto-hint">
                  To catch what the computer is playing too — a video, a call — tick <strong>Share audio</strong> in
                  the box that opens next.
                </p>
                {store && (
                  <p className="howto-hint">
                    {store.storage === 'drive'
                      ? 'Recordings are kept in your Google Drive.'
                      : `Recordings are kept on the server — ${store.disk_used_mb} MB of ${store.disk_cap_mb} MB used. Connect Google Drive in Settings for more room.`}
                    {' '}Up to {store.max_mb} MB each, which is about twenty minutes.
                  </p>
                )}
              </>
            )}

            {phase === 'ready' && clip && (
              <div className="howto-preview">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video src={clip.url} controls preload="metadata" />
                <div className="howto-preview-meta">{fmt(secs)} · {mb(clip.blob.size)}</div>
                <label className="howto-field">
                  <span>What to call it</span>
                  <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} />
                </label>
                {onTask && (
                  <label className="howto-check">
                    <input type="checkbox" checked={everyCopy} onChange={e => setEvery(e.target.checked)} />
                    <span>Show this on every copy of this task{repeats ? '' : ' (this workflow doesn’t repeat, so it may only stay here)'}</span>
                  </label>
                )}
              </div>
            )}

            {phase === 'saving' && (
              <div className="howto-saving">
                <div className="howto-bar"><div style={{ width: `${progress}%` }} /></div>
                <span>Uploading… {progress}%</span>
              </div>
            )}

            {note && <div className="howto-note">{note}</div>}
            {err && <div className="howto-err">{err}</div>}
          </>
        )}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={close}>{phase === 'saving' ? 'Close' : 'Cancel'}</button>
          {phase === 'idle' && canRecordScreen() && <button className="btn btn-primary" onClick={start}>● Choose a screen and start</button>}
          {phase === 'ready' && (
            <>
              <button className="btn btn-ghost" onClick={download} title="Keep a copy on this computer">⬇ Download</button>
              <button className="btn btn-ghost" onClick={redo}>↺ Record again</button>
              <button className="btn btn-primary" onClick={save}>Save to {onTask ? 'this task' : 'their file'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// The recorder lives above the whole app, not inside the task that started it.
//
// It used to be rendered by the task panel, which meant closing the panel — or going anywhere else
// in the Hub — tore the recorder down mid-recording and threw the video away. That is exactly what
// somebody recording how a task is done is going to do: open Xero, look at another workflow, come
// back. Held up here, the recording survives all of it.
const RecorderCtx = createContext(null);
export const useRecorder = () => useContext(RecorderCtx) || { record: () => {}, recording: false };

export function RecorderProvider({ children }) {
  const [session, setSession] = useState(null);   // { subject, repeats }

  // Closing the tab mid-recording loses it, and the browser is the only thing that can ask first.
  useEffect(() => {
    if (!session) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [session]);

  return (
    <RecorderCtx.Provider value={{ record: (subject, repeats) => setSession({ subject, repeats }), recording: !!session }}>
      {children}
      {session && (
        <RecorderModal
          subject={session.subject}
          repeats={session.repeats}
          onClose={() => setSession(null)}
          onSaved={() => { setSession(null); window.dispatchEvent(new Event('pm-howtos-changed')); }}
        />
      )}
    </RecorderCtx.Provider>
  );
}

// ── What sits on the thing it was recorded about ──────────────
//
// A task, or a client. The two differ in three words — the heading, where the list is fetched
// from, and whether "show on every copy" means anything — and in nothing else, so they are one
// component. Two would be two lists drifting apart a line at a time, and the second one would be
// the one nobody remembered to fix.
function HowToList({ subject, canManage = false, repeats = false }) {
  const [rows, setRows] = useState(null);
  const recorder = useRecorder();
  const [playing, setPlaying] = useState(null);   // id of the one being watched
  const [failed, setFailed] = useState({});       // id -> why it wouldn't play, in words

  const load = useCallback(async () => {
    if (!subject || !subject.id) return;
    try { const r = await api.get(subject.url); setRows(r.data || []); }
    catch { setRows([]); }
  }, [subject]);
  useEffect(() => { load(); }, [load]);
  // Saved from the bar, which is nowhere near this panel — so the list is told rather than asked.
  useEffect(() => {
    window.addEventListener('pm-howtos-changed', load);
    return () => window.removeEventListener('pm-howtos-changed', load);
  }, [load]);

  const remove = async (r) => {
    if (!await askConfirm({ body: `Delete “${r.title}”? The recording itself goes too.`, label: 'Delete it' })) return;
    try { await api.delete(`/howtos/${r.id}`); load(); }
    catch (e) { window.alert((e.response && e.response.data && e.response.data.error) || 'Could not delete that.'); }
  };
  const rename = async (r) => {
    const t = window.prompt('What should this recording be called?', r.title || '');
    if (t == null || !t.trim()) return;
    try { await api.patch(`/howtos/${r.id}`, { title: t.trim() }); load(); }
    catch { window.alert('Could not rename that.'); }
  };
  const toggleEvery = async (r) => {
    try { await api.patch(`/howtos/${r.id}`, { every_copy: !r.every_copy }); load(); }
    catch (e) { window.alert((e.response && e.response.data && e.response.data.error) || 'Could not change that.'); }
  };

  const list = rows || [];
  // Nothing recorded and nothing you can do about it: say nothing rather than take up the room.
  if (!list.length && !canManage) return null;

  return (
    <div className="howto-panel">
      <div className="howto-head">
        <span className="howto-label">🎬 {subject.kind === 'task' ? 'How to do this' : 'Recordings about this client'}</span>
        {list.length > 0 && <span className="howto-count">{list.length}</span>}
        {canManage && (
          <button className="link-btn" onClick={() => recorder.record(subject, repeats)}
            title={subject.kind === 'task'
              ? 'Record your screen doing this task, so whoever picks it up next can watch it'
              : 'Record your screen — how this client’s file is laid out, or anything about them worth showing rather than writing'}>
            {list.length ? 'Record another' : subject.kind === 'task' ? 'Record how it’s done' : 'Record something'}
          </button>
        )}
      </div>

      {list.map(r => (
        <div className="howto-row" key={r.id}>
          {playing === r.id ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video className="howto-video" src={videoSrc(r.id)} controls autoPlay preload="metadata"
              onLoadedMetadata={e => teachItsLength(e.currentTarget)}
              onError={e => setFailed(f => ({ ...f, [r.id]: WHY[(e.currentTarget.error || {}).code] || 'It couldn’t be played.' }))} />
          ) : (
            <button className="howto-play" onClick={() => setPlaying(r.id)}>
              <span className="howto-play-icon">▶</span>
              <span className="howto-play-title">{r.title}</span>
              <span className="howto-play-meta">{r.duration_sec ? fmt(r.duration_sec) : ''}</span>
            </button>
          )}
          {failed[r.id] && (
            <div className="howto-err" style={{ marginTop: 6 }}>
              {failed[r.id]} You can still download it and play it on this computer.
            </div>
          )}
          <div className="howto-meta">
            {playing === r.id && <strong>{r.title}</strong>}
            <span>{r.created_by || 'Someone'} · {when(r.created_at)}{r.size_bytes ? ` · ${mb(r.size_bytes)}` : ''}</span>
            {!!r.every_copy && <span className="howto-badge" title="This shows on every copy of this task, for every client">On every copy</span>}
            <a className="howto-dl" href={`${apiOrigin}/api/howtos/${r.id}/download?t=${encodeURIComponent(getStaffToken() || '')}`}>⬇ Download</a>
            {canManage && <button className="link-btn" onClick={() => rename(r)}>Rename</button>}
            {canManage && subject.kind === 'task' && <button className="link-btn" onClick={() => toggleEvery(r)}>{r.every_copy ? 'Only this task' : 'Show on every copy'}</button>}
            {canManage && <button className="link-btn howto-del" onClick={() => remove(r)}>Delete</button>}
          </div>
        </div>
      ))}

    </div>
  );
}

// A recording about how a task is done. Filed against the task, and — where the workflow came from
// a template — optionally against every client's copy of it.
export default function TaskHowTos({ task, canManage = false, repeats = false }) {
  const subject = useMemo(() => (task && task.id
    ? { kind: 'task', id: task.id, name: task.title, url: `/work/tasks/${task.id}/howtos` }
    : null), [task]);
  if (!subject) return null;
  return <HowToList subject={subject} canManage={canManage} repeats={repeats} />;
}

// And one about the client themselves.
//
// Not everything worth recording is a step in a month-end. How their file is laid out, why the
// payroll is done the way it is, what the owner asked for on a call — none of that belongs to a
// task, and filing it against whichever task happened to be open is how it is never found again.
export function ClientHowTos({ client, canManage = false }) {
  const subject = useMemo(() => (client && client.id
    ? { kind: 'client', id: client.id, name: client.name, url: `/clients/${client.id}/howtos` }
    : null), [client]);
  if (!subject) return null;
  return <HowToList subject={subject} canManage={canManage} />;
}
